/**
 * EL GRABADOR (spec §4.12.2): la misma captura del share en vivo (`lib/share/capture.ts`: pantalla +
 * micrófono + audio del sistema mezclados en UN track) metida en un `MediaRecorder` con el formato
 * de la escalera (`mime.ts`), bitrate por sede y chunks de 1 s que van a la cola de subida.
 *
 * REGLA DE ORO (igual que el share): `beginCapture()` se llama SÍNCRONAMENTE dentro del clic de
 * Start — `getDisplayMedia` exige activación transitoria y cualquier `await` antes la pierde.
 *
 * Tiempo grabado: se cuenta SIN las pausas (`elapsedMs()`), que es lo que ve el cronómetro y lo que
 * usa la auto-pausa para convertir bytes pendientes en segundos de media.
 */
import { CaptureFailed, captureScreen, displayMediaSupported, type Capture, type CaptureSurface } from '../share/capture';
import { probeMime, mimeLadder, type ProbeResult } from './mime';

export const MAX_RECORDING_MS = 30 * 60_000;
export const WARN_AT_MS = 25 * 60_000;
export const TIMESLICE_MS = 1_000;
export const AUDIO_KBPS = 96;
export const DEFAULT_VIDEO_KBPS = 2_500;
export const LOW_UPLINK_VIDEO_KBPS = 1_500;

/**
 * Bitrate de video por sede (spec §0 "Record: formato" [rev.3f]): 2.500 kbps en Georgia/Canadá/España
 * y 1.500 en Caracas/Manila hasta que F0 mida el uplink real (entonces: min(2.500, 0,7 × uplink)).
 * Recibe el nombre de la sede tal como está en `time_locations.name` ("Venezuela", "Filipinas"…).
 */
export function bitrateForLocation(location: string | null | undefined): number {
  const l = String(location ?? '').trim().toLowerCase();
  if (!l) return DEFAULT_VIDEO_KBPS;
  if (/venezuela|caracas|filipinas|philippines|manila/.test(l)) return LOW_UPLINK_VIDEO_KBPS;
  return DEFAULT_VIDEO_KBPS;
}

/** ¿Este navegador puede grabar? Captura + MediaRecorder. */
export function recordingSupported(): boolean {
  return displayMediaSupported() && typeof MediaRecorder !== 'undefined';
}

export type RecorderOpts = {
  withMic: boolean;
  micDeviceId?: string;
  withSystemAudio: boolean;
};

/**
 * Paso 1, dentro del clic: abre el picker nativo (y pide el micrófono al cerrarse). Devuelve la
 * promesa de la captura; `CaptureFailed('cancelled')` si canceló el picker.
 */
export function beginCapture(opts: RecorderOpts): Promise<Capture> {
  if (!recordingSupported()) return Promise.reject(new CaptureFailed('unsupported'));
  try {
    return captureScreen({ withMic: opts.withMic, micDeviceId: opts.micDeviceId, selfBrowser: 'include', withSystemAudio: opts.withSystemAudio });
  } catch (e) {
    return Promise.reject(e);
  }
}

export type Recorder = {
  mime: string;
  mimeFallback: boolean;
  surface: CaptureSurface | null;
  hasMic: boolean;
  hasSystemAudio: boolean;
  /** Dimensiones del track de video (para `complete`). */
  size(): { width: number | null; height: number | null };
  /** Arranca de verdad (tras la cuenta regresiva). */
  start(): void;
  pause(): void;
  resume(): void;
  paused(): boolean;
  setMuted(m: boolean): void;
  isMuted(): boolean;
  /** Milisegundos grabados sin contar pausas. */
  elapsedMs(): number;
  /** Para el grabador; el último `dataavailable` llega antes de resolver. */
  stop(): Promise<void>;
  /** Suelta pantalla y micrófono (tras stop, o al cancelar). */
  release(): void;
  onData(cb: (blob: Blob) => void): void;
  /** La barra nativa "Stop sharing" o la ventana compartida que se cerró. */
  onEnded(cb: () => void): void;
};

/**
 * Paso 2: elige el formato con una sonda real y arma el `MediaRecorder` (sin arrancarlo: el
 * arranque es después de la cuenta regresiva). Devuelve null si ningún mime arranca.
 */
export function createRecorder(cap: Capture, opts: { videoKbps: number; probe?: ProbeResult | null }): Recorder | null {
  const ladder = mimeLadder((m) => MediaRecorder.isTypeSupported(m));
  const probe = opts.probe ?? probeMime(cap.stream, ladder);
  if (!probe) return null;
  const dataCbs: Array<(b: Blob) => void> = [];
  const endedCbs: Array<() => void> = [];
  let rec: MediaRecorder | null = null;
  let startedAt = 0;
  let pausedAt = 0;
  let pausedTotal = 0;
  let stopping: { resolve: () => void } | null = null;
  let released = false;
  // Las dimensiones se leen al ARRANCAR: tras `stop()` el track está parado y `getSettings()` vuelve
  // vacío (medido en el e2e: `complete` salía con width/height undefined).
  let size: { width: number | null; height: number | null } = { width: null, height: null };
  const readSize = () => {
    const st = cap.videoTrack.getSettings();
    if (st.width || st.height) size = { width: st.width ?? null, height: st.height ?? null };
    return size;
  };

  cap.onEnded(() => { for (const cb of endedCbs) { try { cb(); } catch { /* */ } } });

  const elapsedMs = () => {
    if (!startedAt) return 0;
    const now = Date.now();
    const paused = pausedAt ? now - pausedAt : 0;
    return Math.max(0, now - startedAt - pausedTotal - paused);
  };

  return {
    mime: probe.mime,
    mimeFallback: probe.mimeFallback,
    surface: cap.surface,
    hasMic: cap.mic === 'on',
    hasSystemAudio: cap.hasSystemAudio,
    size: () => readSize(),
    start() {
      if (rec) return;
      readSize();
      rec = new MediaRecorder(cap.stream, {
        mimeType: probe.mime,
        videoBitsPerSecond: opts.videoKbps * 1000,
        audioBitsPerSecond: AUDIO_KBPS * 1000,
      });
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size) for (const cb of dataCbs) { try { cb(e.data); } catch { /* */ } }
      };
      rec.onstop = () => { stopping?.resolve(); stopping = null; };
      rec.onerror = () => { /* el `ended` del track o el stop del usuario cierran; nada que tirar acá */ };
      startedAt = Date.now();
      rec.start(TIMESLICE_MS);
    },
    pause() {
      if (!rec || rec.state !== 'recording') return;
      try { rec.pause(); } catch { return; }
      pausedAt = Date.now();
    },
    resume() {
      if (!rec || rec.state !== 'paused') return;
      try { rec.resume(); } catch { return; }
      if (pausedAt) { pausedTotal += Date.now() - pausedAt; pausedAt = 0; }
    },
    paused: () => rec?.state === 'paused',
    setMuted: (m) => cap.setMuted(m),
    isMuted: () => cap.isMuted(),
    elapsedMs,
    stop() {
      if (!rec || rec.state === 'inactive') return Promise.resolve();
      if (pausedAt) { pausedTotal += Date.now() - pausedAt; pausedAt = 0; }
      return new Promise<void>((resolve) => {
        stopping = { resolve };
        try { rec!.stop(); } catch { resolve(); }
        // Red de seguridad: un `onstop` que nunca llega no puede colgar el Stop.
        setTimeout(() => { if (stopping) { stopping = null; resolve(); } }, 4_000);
      });
    },
    release() {
      if (released) return;
      released = true;
      try { if (rec && rec.state !== 'inactive') rec.stop(); } catch { /* */ }
      cap.stop();
    },
    onData(cb) { dataCbs.push(cb); },
    onEnded(cb) { endedCbs.push(cb); },
  };
}

/** Segundos que faltan para el tope de 30 min (para el aviso de 25 y el corte). */
export function limitState(elapsedMs: number): { warn: boolean; reached: boolean } {
  return { warn: elapsedMs >= WARN_AT_MS, reached: elapsedMs >= MAX_RECORDING_MS };
}

/**
 * Título por defecto: "Recording 2026-09-04 14:05" / "Grabación 2026-09-04 14:05" (con `t`, en el
 * idioma del que graba: es lo que queda en el DM, la tarjeta y el nombre del archivo en Files).
 */
export function defaultTitle(now: Date = new Date(), t?: (k: string, vars?: Record<string, string | number>) => string): string {
  const p = (n: number) => String(n).padStart(2, '0');
  const date = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())} ${p(now.getHours())}:${p(now.getMinutes())}`;
  return t ? t('rec.defaultTitle', { date }) : `Recording ${date}`;
}
